"use client";
import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Twitter, Instagram, Linkedin } from "lucide-react";
import Link from "next/link";
import BlogService from "@/app/services/blog";
import { BASE_URL } from "@/app/contants/url";
import ArticleError from "@/app/components/error/ArticleError";
import NewsSkeleton from "@/app/components/skeleton/NewsSkeleton";
import { useTranslations, useLocale } from 'next-intl';

const colors = ["bg-[#ffd0f6]", "bg-[#b7dfff]", "bg-[#cbb2ba]", "bg-[#c7e1f8]"]

export default function News() {
    const t = useTranslations('News');
    const locale = useLocale();
    const carouselRef = useRef<HTMLDivElement>(null);
    const [data, setData] = useState<any[]>([])
    const [error, setError] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [categories, setCategories] = useState<any[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
    const [meta, setMeta] = useState<any>(null)
    const [page, setPage] = useState<number>(1)

    async function getCategoriesList() {
        try {
            const body = {
                no_pagination: 1
            }
            const result: any = await BlogService.getCategories(body);
            if (result?.data?.success) {
                setCategories(result?.data?.data)
            }
        } catch (error: any) {
        } finally {
        }
    }

    async function getData() {
        if (data?.length <= 0) { setIsLoading(true) }
        try {
            const body: any = {
                page: page,
                page_size: 16,
                language: locale,
                sort: 'desc'
            }
            if (selectedCategory) { body.category_id = selectedCategory }
            const result: any = await BlogService.getList(body);
            if (result?.data?.success) {
                setData(result?.data?.data);
                setMeta(result?.data?.meta)
                setError(false)
            } else {
                setError(true)
            }
        } catch (error: any) {
            setError(true)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => { getCategoriesList() }, [])
    useEffect(() => { getData() }, [page, selectedCategory])

    return (
        <section className="bg-[#120428] py-20 px-6 md:px-16 text-white min-h-screen">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
                <div>
                    <h2 className="text-6xl font-bold tracking-tight">{t('articles')}</h2>
                </div>
                <div className="flex gap-3">
                    <Link href='https://www.instagram.com/primalt.ai/' target="_blank" className="cursor-pointer"><SocialPill icon={<Instagram size={16} />} label="Instagram" active /></Link>
                    <Link href='https://www.linkedin.com/company/primalt/' target="_blank" className="cursor-pointer"><SocialPill icon={<Linkedin size={16} />} label="LinkedIn" /></Link>
                </div>
            </div>
            {categories?.length > 0 && (
                <>
                    <div className="flex flex-col md:flex-row gap-3 mb-10">
                        <button onClick={() => { setSelectedCategory(null) }}><SocialPill icon={<></>} label="All" active={!selectedCategory} /></button>
                        {categories?.map((cat: any) => (
                            <button onClick={() => { setSelectedCategory(cat?.id) }}><SocialPill icon={<></>} label={`${cat[`name_${locale}`]}`} active={selectedCategory == cat?.id} /></button>
                        ))}
                    </div>
                </>
            )}
            {isLoading ? (
                <>
                    <NewsSkeleton />
                    <NewsSkeleton />
                    <NewsSkeleton />
                </>
            ) : (
                <>
                    {error ? (
                        <ArticleError errorMessage={t('articles_error')} displayButton={false} />
                    ) : (
                        <>
                            {data?.length <= 0 ? (
                                <div className="text-center mt-2">
                                    {t('articles_no_data')}
                                </div>
                            ) : (
                                <>
                                    <div
                                        ref={carouselRef}
                                        className="grid grid-cols-1 md:grid-cols-3 pb-10 gap-2 md:gap-8"
                                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                                    >
                                        {data?.map((post: any, index: number) => (
                                            <div
                                                key={post.id}
                                                className="flex-none w-[100%] snap-start rounded-b-none"
                                            >
                                                <div className="rounded-[20px] rounded-b-none overflow-hidden md:h-[550px] flex flex-col shadow-2xl">
                                                    <div className="h-[40%] md:h-[70%] relative">
                                                        <img src={`${BASE_URL}storage/${post?.cover_path}`} alt={post.title} className="w-full h-full object-cover" />
                                                    </div>

                                                    {/* Content Section */}
                                                    <div className={`${colors[index % colors.length]} p-4 md:p-10 text-black flex flex-col justify-between rounded-b-none`}>
                                                        <p className="text-lg font-medium leading-snug">
                                                            {post?.title}
                                                        </p>
                                                        <Link href={`/blog/${post?.slug}`} className="text-sm mt-2 font-bold underline hover:opacity-70 transition-opacity text-left cursor-pointer">
                                                            {t('read_article')}
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex items-center justify-center mt-8 gap-6">
                                        <button
                                            onClick={() => setPage(page - 1)}
                                            className="p-2 text-gray-500 hover:text-white transition-colors disabled:opacity-20"
                                            disabled={page === 0}
                                        >
                                            <ChevronLeft size={40} strokeWidth={1} />
                                        </button>

                                        {/* Dots Navigation */}
                                        <div className="flex gap-3">
                                            {Array.from({ length: meta.last_page }, (_, i) => {
                                                const pageNumber = i + 1;
                                                return (
                                                    <div
                                                        key={pageNumber}
                                                        onClick={() => setPage(pageNumber)}
                                                        className={`w-2.5 h-2.5 rounded-full cursor-pointer transition-all duration-300 ${pageNumber === meta.current_page
                                                                ? "bg-white scale-125"
                                                                : "bg-gray-700 hover:bg-gray-500"
                                                            }`}
                                                    />
                                                );
                                            })}
                                        </div>

                                        <button
                                            onClick={() => setPage(page + 1)}
                                            className="p-2 text-gray-500 hover:text-white transition-colors disabled:opacity-20"
                                            disabled={page === meta?.last_page}
                                        >
                                            <ChevronRight size={40} strokeWidth={1} />
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </>
            )}


        </section>
    );
}

function SocialPill({ icon, label, active = false }: { icon: any, label: string, active?: boolean }) {
    return (
        <button className={`flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-all
      ${active ? "bg-[#7C4DFF] border-[#7C4DFF]" : "border-gray-800 hover:border-gray-600 bg-white/5"}`}>
            {label} {icon}
        </button>
    );
}